import type { ApiResponse, PaginatedResponse } from '@/types/api.types'
import type {
  Assessment,
  AssessmentCompleto,
  AssessmentCreate,
  AssessmentListParams,
  AssessmentUpdate
} from '@/types/assessment.types'
import type { DashboardStatsData, DashboardStatsParams } from '@/types/dashboard_stats.types'

import { apiClient, ensureValidToken } from '../client'

export class AssessmentService {
  // =============================================================================
  // ASSESSMENTS ENDPOINTS
  // =============================================================================

  async getAssessments(params?: AssessmentListParams): Promise<PaginatedResponse<Assessment>> {
    await ensureValidToken()

    const queryParams = new URLSearchParams()
    if (params?.search) queryParams.append('search', params.search)
    if (params?.organizacion_id) queryParams.append('organizacion_id', params.organizacion_id)
    if (params?.version_id) queryParams.append('version_id', params.version_id)
    if (params?.workflow_estado) queryParams.append('workflow_estado', params.workflow_estado)
    if (params?.fase_actual) queryParams.append('fase_actual', params.fase_actual)
    if (params?.es_inicial !== undefined && params.es_inicial !== null) {
      queryParams.append('es_inicial', params.es_inicial.toString())
    }
    if (params?.numero_ciclo !== undefined && params.numero_ciclo !== null) {
      queryParams.append('numero_ciclo', params.numero_ciclo.toString())
    }
    if (params?.expand_all !== undefined) {
      queryParams.append('expand_all', params.expand_all.toString())
    }
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())

    const response = await apiClient.get<PaginatedResponse<Assessment>>(
      `/api/v1/assessments${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    )

    return response.data
  }

  async createAssessment(assessmentData: AssessmentCreate): Promise<Assessment> {
    await ensureValidToken()

    const response = await apiClient.post<ApiResponse<Assessment>>(
      '/api/v1/assessments',
      assessmentData
    )

    if (response.data && response.data.data) {
      return response.data.data
    }

    throw new Error('Invalid create assessment response')
  }

  async getAssessmentById(assessmentId: string, expandAll?: boolean): Promise<AssessmentCompleto> {
    await ensureValidToken()

    const response = await apiClient.get<ApiResponse<AssessmentCompleto>>(
      `/api/v1/assessments/${assessmentId}`,
      {
        params: {
          expand_all: expandAll ? 'true' : 'false'
        }
      }
    )

    if (response.data && response.data.data) {
      return response.data.data
    }

    throw new Error('Invalid get assessment response')
  }

  async updateAssessment(
    assessmentId: string,
    assessmentData: AssessmentUpdate
  ): Promise<Assessment> {
    await ensureValidToken()

    const response = await apiClient.put<ApiResponse<Assessment>>(
      `/api/v1/assessments/${assessmentId}`,
      assessmentData
    )

    if (response.data && response.data.data) {
      return response.data.data
    }

    throw new Error('Invalid update assessment response')
  }

  async deleteAssessment(assessmentId: string): Promise<void> {
    await ensureValidToken()

    await apiClient.delete(`/api/v1/assessments/${assessmentId}`)
  }

  async getStats(params: DashboardStatsParams = {}): Promise<DashboardStatsData> {
    await ensureValidToken()

    const queryParams = new URLSearchParams()
    if (params.inactividad_dias !== undefined)
      queryParams.append('inactividad_dias', params.inactividad_dias.toString())
    if (params.vencimiento_dias !== undefined)
      queryParams.append('vencimiento_dias', params.vencimiento_dias.toString())
    if (params.produccion_dias !== undefined)
      queryParams.append('produccion_dias', params.produccion_dias.toString())
    if (params.organizacion_id) queryParams.append('organizacion_id', params.organizacion_id)
    if (params.limite_prioritarios !== undefined)
      queryParams.append('limite_prioritarios', params.limite_prioritarios.toString())

    const response = await apiClient.get<ApiResponse<DashboardStatsData>>(
      `/api/v1/assessments/stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    )

    if (response.data?.data) {
      return response.data.data
    }

    throw new Error('Invalid stats response')
  }
}

export const assessmentService = new AssessmentService()
